// SPDX-License-Identifier: MIT
import type { PlayerFont } from '@pulsereel/schema';

let fallback: Promise<void> | undefined;
const supplied = new Map<string, FontFace>();

/** Independent hosts keep the same self-hosted CSS fallback; never loosen CSP. */
export function fallbackFonts(): Promise<void> {
  fallback ??= new Promise<void>((resolve) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'fonts.css';
    link.onload = () => resolve();
    link.onerror = () => resolve(); // Preserve the existing system-font fallback on a failed asset.
    document.head.append(link);
  });
  return fallback;
}

/** Register in manifest order (last matching range wins), atomically after decoding all faces. */
export async function receiveFonts(fonts: PlayerFont[] | undefined, replace = true): Promise<void> {
  if (!fonts) {
    if (replace) {
      for (const face of supplied.values()) document.fonts.delete(face);
      supplied.clear();
      await fallbackFonts();
    }
    return;
  }
  const faces: Array<[string, FontFace]> = [];
  try {
    for (const { family, weight, unicodeRange, data } of fonts) {
      const face = new FontFace(family, data, { weight, unicodeRange, style: 'normal', display: 'block' });
      faces.push([JSON.stringify([family, weight, unicodeRange]), face]);
    }
    await Promise.all(faces.map(([, face]) => face.load()));
  } catch {
    // Invalid binary or unsupported buffer fonts: fall back without adding blob:/data: to font-src.
    for (const face of supplied.values()) document.fonts.delete(face);
    supplied.clear();
    await fallbackFonts();
    return;
  }
  if (replace) {
    for (const face of supplied.values()) document.fonts.delete(face);
    supplied.clear();
  }
  for (const [key, face] of faces) {
    const previous = supplied.get(key);
    if (previous) document.fonts.delete(previous);
    document.fonts.add(face);
    supplied.set(key, face);
  }
}
